import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";

export interface User {
  id: string;
  name: string;
  email?: string;
  role?: string;
  status?: string;
}

interface UserContextType {
  user: User | null;
  accessToken: string | null;
  setUserAndToken: (user: User | null, accessToken: string | null) => void;
  loading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const setUserAndToken = (user: User | null, token: string | null) => {
    setUser(user);
    setAccessToken(token);
  };

  useEffect(() => {
    // Skip auth bootstrap on public pages
    if (typeof window !== 'undefined' && 
        (window.location.pathname.startsWith('/public/') || 
         window.location.pathname.startsWith('/org/'))) {
      setLoading(false);
      return;
    }
    // On mount, if no user, try to refresh session using refresh token cookie
    if (!user) {
      const refreshSession = async () => {
        try {
          const resp = await fetch(
            process.env.REACT_APP_BACKEND_BASE_URL + "/api/v1/admin/refresh",
            {
              method: "POST",
              credentials: "include",
            }
          );
          if (resp.ok) {
            const data = await resp.json();
            setUser(data.user);
            setAccessToken(data.access_token);
          } else {
            setUser(null);
            setAccessToken(null);
          }
        } catch {
          setUser(null);
          setAccessToken(null);
        } finally {
          setLoading(false);
        }
      };
      refreshSession();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <UserContext.Provider value={{ user, accessToken, setUserAndToken, loading }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}; 